import { MoreHorizontal } from "lucide-react";
import { useTran } from "@/hooks";
import { cn } from "@/lib/utils";
import { TARGET_ACCENT } from "../domains/theme";
import { formatPercentage } from "../domains/formatting";

const COLOR_GOOD = "#10b981";
const COLOR_BAD = "#3b82f6";
const COLOR_NEUTRAL = "#94a3b8";

const SCALE_LO = -100;
const SCALE_HI = 100;
const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
const positionOf = (val) => ((val - SCALE_LO) / (SCALE_HI - SCALE_LO)) * 100;

const formatPct = (v) => {
  const sign = v > 0 ? "+" : "";
  return `${sign}${formatPercentage(v)}`;
};

// gapPct is normalized so positive = on the good side (right, green).
// When |gapPct| > 100 the marker moves past the bar edge: an ellipsis
// glyph sits just outside the bar, with the circle marker further out.
const GapGaugeBar = ({ gapPct }) => {
  const { t } = useTran();
  const hasValue = Number.isFinite(gapPct);
  const targetPos = 50;
  const overflowRight = hasValue && gapPct > SCALE_HI;
  const overflowLeft = hasValue && gapPct < SCALE_LO;
  const overflowed = overflowRight || overflowLeft;
  const markerPos = hasValue
    ? positionOf(clamp(gapPct, SCALE_LO, SCALE_HI))
    : 50;

  const isGood = hasValue && gapPct >= 0;
  const markerColor = !hasValue
    ? COLOR_NEUTRAL
    : isGood
      ? COLOR_GOOD
      : COLOR_BAD;

  const trackGradient =
    "linear-gradient(to right, rgba(59,130,246,0.22) 0%, rgba(59,130,246,0.08) 40%, rgba(148,163,184,0.10) 50%, rgba(16,185,129,0.08) 60%, rgba(16,185,129,0.22) 100%)";

  const labelLeftPos = clamp(markerPos, 4, 96);
  const labelShift =
    markerPos <= 4
      ? "translate-x-0"
      : markerPos >= 96
        ? "-translate-x-full"
        : "-translate-x-1/2";
  const labelStyle = overflowed
    ? { left: overflowRight ? "calc(100% + 36px)" : "-36px" }
    : { left: `${labelLeftPos}%` };
  const labelTransform = overflowed ? "-translate-x-1/2" : labelShift;

  return (
    <div className="w-full select-none">
      <div className="relative h-4">
        {hasValue && (
          <span
            className={cn(
              "absolute bottom-0 whitespace-nowrap text-[11px] font-bold leading-none tabular-nums",
              labelTransform,
              isGood
                ? "text-emerald-600 dark:text-emerald-300"
                : "text-blue-600 dark:text-blue-300",
            )}
            style={labelStyle}
          >
            {formatPct(gapPct)}
          </span>
        )}
      </div>

      <div className="relative h-6">
        <div className="absolute inset-x-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-slate-200/80 ring-1 ring-inset ring-slate-300/70 dark:bg-slate-700/40 dark:ring-slate-600/50" />
        <div
          className="absolute inset-x-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full"
          style={{ background: trackGradient }}
        />

        <span
          className="absolute top-1/2 h-5 w-[3px] -translate-x-1/2 -translate-y-1/2 rounded-full ring-1 ring-amber-700/60 dark:ring-amber-200/40"
          style={{ left: `${targetPos}%`, backgroundColor: TARGET_ACCENT }}
        />

        {hasValue && !overflowed && (
          <>
            <span
              className="pointer-events-none absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full opacity-50 blur-[4px]"
              style={{ left: `${markerPos}%`, backgroundColor: markerColor }}
            />
            <span
              className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-sm ring-1 ring-slate-300/40 transition-all dark:border-slate-900 dark:ring-slate-500/40"
              style={{ left: `${markerPos}%`, backgroundColor: markerColor }}
            />
          </>
        )}

        {hasValue && overflowed && (
          <>
            <MoreHorizontal
              aria-hidden
              strokeWidth={3}
              className="pointer-events-none absolute top-1/2 h-5 w-5 -translate-y-1/2 drop-shadow"
              style={{
                [overflowRight ? "left" : "right"]: "100%",
                [overflowRight ? "marginLeft" : "marginRight"]: "4px",
                color: markerColor,
              }}
            />
            <span
              className="pointer-events-none absolute top-1/2 h-3 w-3 -translate-y-1/2 rounded-full opacity-50 blur-[4px]"
              style={{
                [overflowRight ? "left" : "right"]: "calc(100% + 30px)",
                backgroundColor: markerColor,
              }}
            />
            <span
              className="absolute top-1/2 h-3 w-3 -translate-y-1/2 animate-pulse rounded-full border-2 border-white shadow-sm ring-1 ring-slate-300/40 dark:border-slate-900 dark:ring-slate-500/40"
              style={{
                [overflowRight ? "left" : "right"]: "calc(100% + 30px)",
                backgroundColor: markerColor,
              }}
            />
          </>
        )}
      </div>

      <div className="relative mt-1 h-3 text-[10px] leading-none">
        <span className="absolute left-0 text-muted-foreground/80 tabular-nums">
          -100%
        </span>
        <span
          className="absolute -translate-x-1/2 font-semibold tabular-nums"
          style={{ left: `${targetPos}%`, color: TARGET_ACCENT }}
        >
          {t("Target")}
        </span>
        <span className="absolute right-0 text-muted-foreground/80 tabular-nums">
          +100%
        </span>
      </div>
    </div>
  );
};

export default GapGaugeBar;
